import { useState, useEffect } from 'react';
import { isTerminalMatterStatus } from '@/lib/matterStatus';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  MagnifyingGlass as Search,
  Briefcase,
  Plus,
  Kanban,
  PushPin,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MatterStatusBadge } from '@/components/shared/MatterStatusBadge';
import { MatterHealthBadge, calculateMatterHealth } from '@/components/shared/MatterHealthBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { TablePagination, pageSlice, PAGE_SIZE_OPTIONS } from '@/components/shared/TablePagination';
import { StatCard } from '@/components/shared/StatCard';
import { RowActions } from '@/components/shared/RowActions';
import { EditMatterModal } from '@/components/shared/EditMatterModal';
import { useMattersStore, type Matter } from '@/lib/mattersStore';
import { useMatterQuickAccessStore } from '@/lib/matterQuickAccessStore';
import { PinnedMattersBar } from '@/components/shared/PinnedMattersBar';
import { archiveMatter, restoreMatter, searchMatters } from '@/lib/mattersApi';
import { fetchDashboardMetrics } from '@/lib/meApi';
import { useDebounce } from '@/lib/useDebounce';
import { useAuthStore } from '@/features/auth/store';
import { canCreateMatter, canUpdateMatter } from '@/lib/pupilUiBoundary';
import { useChambersMode } from '@/lib/useChambersMode';
import { onAllocationUpdate, onMattersUpdate } from '@/lib/websocket';

function hearingUrgency(iso: string | null | undefined): 'urgent' | 'attention' | null {
  if (!iso) return null;
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days <= 3) return 'urgent';
  if (days <= 7) return 'attention';
  return null;
}

type Tab = 'active' | 'lead' | 'junior' | 'closed' | 'archived';

export default function MyCasesPage({ defaultTab = 'active', archivedOnly = false }: { defaultTab?: Tab; archivedOnly?: boolean } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab) ?? defaultTab;
  const filterText = searchParams.get('q') ?? '';
  const setTab = (t: Tab) => setSearchParams((p) => { p.set('tab', t); p.delete('q'); return p; }, { replace: true });
  const setFilterText = (v: string) => setSearchParams((p) => { v ? p.set('q', v) : p.delete('q'); return p; }, { replace: true });
  const [editMatter, setEditMatter] = useState<Matter | null>(null);
  const navigate = useNavigate();
  const allMatters = useMattersStore((s) => s.matters);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  const updateMatter = useMattersStore((s) => s.updateMatter);
  const pinnedMatters = useMatterQuickAccessStore((s) => s.pinnedMatters);
  const unpinMatterRow = useMatterQuickAccessStore((s) => s.unpinMatter);
  const mattersLoading = useMattersStore((s) => s.loading);
  const mattersError = useMattersStore((s) => s.error);
  const mattersTruncated = useMattersStore((s) => s.truncated);
  useEffect(() => {
    fetchMatters();
    const poll = setInterval(fetchMatters, 300_000);
    const unsubAllocation = onAllocationUpdate(() => fetchMatters());
    const unsubMatters = onMattersUpdate(() => fetchMatters());
    return () => { clearInterval(poll); unsubAllocation(); unsubMatters(); };
  }, [fetchMatters]);
  const userId = useAuthStore((s) => s.user?.id);
  // Matters are READ-ONLY for a pupil: POST /matters answers 403 (no case.create).
  const canOpenMatter = canCreateMatter(useAuthStore((s) => s.user?.memberType));
  // …and so do PATCH /matters/:id, /archive and /restore (no case.update.*). The row Edit goes
  // through the OPTIMISTIC `updateMatter`, so a pupil saw the edit apply and then silently revert
  // under "please try again"; Archive / Restore await the API and 403 outright. Withhold all three.
  const canChangeMatter = canUpdateMatter(useAuthStore((s) => s.user?.memberType));
  const chambersMode = useChambersMode();

  // Restrict any list (store or search results) to matters where this barrister
  // is lead or junior, and derive the actual role so tab filtering is accurate.
  const toMyMatters = (list: Matter[]) =>
    list
      .filter((m) => m.leadBarristerUserId === userId || m.juniorBarristerUserId === userId)
      .map((m) => ({ ...m, role: m.leadBarristerUserId === userId ? 'lead' : 'junior' }));

  // Stat cards always reflect the FULL caseload from the store — never the
  // search-narrowed list.
  const myMatters = toMyMatters(allMatters);

  const activeCount = myMatters.filter((c) => !isTerminalMatterStatus(c.status)).length;
  const leadCount = myMatters.filter((c) => c.role === 'lead').length;
  const juniorCount = myMatters.filter((c) => c.role === 'junior').length;
  // Win rate is a backend-owned fact (canonical computeWinRate; null below a 3-matter sample).
  // The UI renders it — it never recomputes from the matter list. Shares the dashboard query key.
  const metricsQ = useQuery({ queryKey: ['dashboard-metrics'], queryFn: fetchDashboardMetrics });
  const winRate = metricsQ.data?.practiceHealth.winRate ?? null;

  // When the user types a query, hit the server-side FTS endpoint (the same one
  // the admin Matters page uses) so search covers EVERY matter — including older
  // ones not currently loaded into the store — matching number, title,
  // description, opponent AND the client/solicitor name. Below 2 chars we stay
  // on the in-memory store so the list is instant.
  const debouncedSearch = useDebounce(filterText.trim(), 300);
  const searching = debouncedSearch.length >= 2;
  const searchQ = useQuery({
    queryKey: ['my-matters-search', debouncedSearch],
    queryFn: () => searchMatters(debouncedSearch),
    enabled: searching,
    staleTime: 30_000,
  });

  // Source list: server results when searching, full store otherwise.
  const sourceMatters = searching ? toMyMatters(searchQ.data ?? []) : myMatters;

  const filtered = sourceMatters.filter((c) =>
    // Tab still applies on top of the search results. Text matching is done by
    // the server when searching, so there is no client-side text filter here.
    tab === 'active'   ? !isTerminalMatterStatus(c.status) :
    tab === 'archived' ? c.status === 'archived' :
    tab === 'closed'   ? (isTerminalMatterStatus(c.status) && c.status !== 'archived') :
    c.role === tab,
  );

  // Pinned matters float to the top of the list.
  const pinnedIds = new Set(pinnedMatters.map((p) => p.id));
  filtered.sort((a, b) => (pinnedIds.has(b.id) ? 1 : 0) - (pinnedIds.has(a.id) ? 1 : 0));

  // ── Paging ───────────────────────────────────────────────────────────────────
  // A practice of any size outgrows one screen of matters. Changing tab or search starts
  // again at page one, so you are never left looking at an empty page 7 of a 2-page list.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]!);
  useEffect(() => { setPage(1); }, [tab, debouncedSearch, archivedOnly]);
  const pagedMatters = pageSlice(filtered, page, pageSize);

  const handleArchive = async (c: Matter) => {
    try {
      await archiveMatter(c.id);
      await fetchMatters();
      toast.success('Matter archived');
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? err?.response?.data?.message ?? 'Could not archive matter';
      const blockedByHearings = /hearing/i.test(msg);
      toast.error(msg, blockedByHearings ? {
        duration: 10_000,
        action: {
          label: 'Vacate hearings',
          onClick: () => navigate(`/barrister/matters/${c.id}?tab=hearings`),
        },
      } : undefined);
    }
  };

  const handleRestore = async (c: Matter) => {
    try {
      await restoreMatter(c.id);
      await fetchMatters();
      toast.success('Matter restored to active');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? err?.response?.data?.message ?? 'Could not restore matter');
    }
  };

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title={archivedOnly ? 'Archived Matters' : 'My Matters'}
        subtitle={archivedOnly
          ? 'Matters you have archived — open one and restore it to bring it back to active'
          : chambersMode.isSolo
            ? 'All your active and closed matters'
            : 'Your personal matters and chambers-assigned briefs — your full caseload'}
        actions={archivedOnly ? undefined : (
          <>
            <Button variant="outline" size="md" leftIcon={<Kanban className="h-4 w-4" />} onClick={() => navigate('/barrister/matters/pipeline')}>
              Pipeline
            </Button>
            {canOpenMatter && <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => navigate('/barrister/matters/new')}>
              {chambersMode.isSolo ? 'New matter' : 'New personal matter'}
            </Button>}
          </>
        )}
      />

      <Card className="mb-4 p-3">
        <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            placeholder="Search matters — name, number, description, client, opponent…"
            className="h-9 flex-1 bg-transparent text-sm focus:outline-none"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          {searching && searchQ.isFetching && (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
          )}
        </div>
      </Card>

      {!archivedOnly && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <StatCard icon={<Briefcase className="h-4 w-4" />} label="Active"    value={String(activeCount)} />
          <StatCard label="As lead"    value={String(leadCount)} />
          <StatCard label="As junior"  value={String(juniorCount)} />
          <StatCard label="Win rate"   value={winRate === null ? '—' : `${winRate}%`} />
        </div>
      )}

      {/* Scrollable, like the contacts and hearing tabs: at phone width these five tabs are
          wider than the screen, and without this "Archived" sat past the right edge with no
          way to reach it. `shrink-0` keeps each tab its own width instead of squashing. */}
      {!archivedOnly && (
        <div className="no-scrollbar -mb-px mb-4 flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b border-border">
          {([
            { key: 'active'   as const, label: 'Active'    },
            { key: 'lead'     as const, label: 'As lead'   },
            { key: 'junior'   as const, label: 'As junior' },
            { key: 'closed'   as const, label: 'Closed'    },
            { key: 'archived' as const, label: 'Archived'  },
          ]).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`shrink-0 border-b-2 px-4 py-2.5 text-sm font-medium transition ${
                tab === t.key ? 'border-brand-600 text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Pinning happens on the matter page and its toast says "find it on the Matters page" —
          this is that promise kept. Without it a barrister saw no trace of what they pinned. */}
      <PinnedMattersBar basePath="/barrister" navigate={navigate} />

      {mattersError && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          <span className="font-semibold">Could not load matters.</span>
          <span className="text-red-600 dark:text-red-400">Check your connection and</span>
          <button onClick={() => fetchMatters()} className="font-semibold underline">try again</button>.
        </div>
      )}

      {/* INCOMPLETE is not the same as EMPTY. The store loads at most `MATTERS_FETCH_LIMIT`
          (200) matters, newest first, and publishes `truncated` for exactly this reason. The
          clerk/admin list has warned about it for a while (`MattersListPage.tsx`); this page
          never did, so a barrister past 200 matters silently lost the OLDEST ones — and because
          the sort is newest-first, the Closed and Archived tabs are precisely where they
          disappeared. Search hits the server, so it still finds them. */}
      {mattersTruncated && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          <span className="font-semibold">Showing your 200 most recent matters.</span>
          <span className="text-amber-700 dark:text-amber-300">Search to find older ones — they exist but are not loaded.</span>
        </div>
      )}

      <Card>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-8"></th>
                <th>Matter #</th>
                <th>Matter name</th>
                <th>Court</th>
                <th>My role</th>
                <th>Next hearing</th>
                <th>Status</th>
              <th className="w-10 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {mattersLoading && allMatters.length === 0 && (
              <tr>
                <td colSpan={8} className="px-6 py-10 text-center text-sm text-muted-foreground">
                  Loading matters…
                </td>
              </tr>
            )}
            {pagedMatters.map((c) => {
              const urgency = hearingUrgency(c.nextHearingIso);
              const health = calculateMatterHealth(c.updatedAt, c.nextHearingIso);
              const pinned = pinnedMatters.some((p) => p.id === c.id);
              return (
              <tr
                key={c.id}
                className={`group border-l-2 ${
                  c.id.startsWith('temp-') ? 'cursor-wait opacity-60' : 'cursor-pointer'
                } ${
                  health.level === 'critical' ? 'border-l-red-500 bg-red-50/30 dark:bg-red-500/10'    :
                  health.level === 'at_risk'  ? 'border-l-amber-400 bg-amber-50/20 dark:bg-amber-500/10' :
                  'border-l-transparent'
                }`}
                onClick={() => { if (!c.id.startsWith('temp-')) navigate(`/barrister/matters/${c.id}`); }}
              >
                <td className="text-center">
                  <MatterHealthBadge
                    lastActivityDate={c.updatedAt}
                    nextHearingDate={c.nextHearingIso}
                    compact
                  />
                </td>
                <td className="whitespace-nowrap text-xs text-brand-700 dark:text-brand-300">{c.number}</td>
                <td>
                  <div className="flex items-start gap-1.5 sm:items-center">
                    {pinned && (
                      <button
                        type="button"
                        title="Pinned — click to unpin"
                        aria-label="Unpin matter"
                        onClick={(e) => { e.stopPropagation(); unpinMatterRow(c.id); toast.success('Matter unpinned'); }}
                        className="mt-0.5 shrink-0 text-brand-600 dark:text-brand-400 transition hover:text-brand-700 dark:hover:text-brand-300 sm:mt-0"
                      >
                        <PushPin className="h-4 w-4" weight="fill" />
                      </button>
                    )}
                    <span className="line-clamp-2 font-medium text-foreground sm:line-clamp-none" title={c.name}>{c.name}</span>
                  </div>
                  {c.area && <div className="mt-0.5 text-[10px] text-muted-foreground">{c.area}</div>}
                </td>
                <td className="whitespace-nowrap text-muted-foreground">{c.court}</td>
                <td><Badge variant={c.role === 'lead' ? 'info' : 'default'}>{c.role === 'lead' ? 'Lead' : 'Junior'}</Badge></td>
                <td>
                  {c.next && c.next !== '—' ? (
                    <span className={`text-xs ${urgency === 'urgent' ? 'font-bold text-red-700 dark:text-red-300' : urgency === 'attention' ? 'font-semibold text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}>
                      {urgency === 'urgent' && <span className="mr-1">●</span>}
                      {c.next}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground/40">—</span>
                  )}
                </td>
                <td><MatterStatusBadge status={c.status} /></td>
                <td onClick={(e) => e.stopPropagation()}>
                  {isTerminalMatterStatus(c.status) ? (
                    <RowActions
                      onView={() => navigate(`/barrister/matters/${c.id}`)}
                      onEdit={canChangeMatter ? () => setEditMatter(c) : undefined}
                      onDelete={canChangeMatter ? () => handleRestore(c) : undefined}
                      deleteLabel="Restore"
                      deleteConfirm={{
                        title: 'Restore matter?',
                        message: `Restore “${c.name}” (${c.number}) to active?`,
                        confirmLabel: 'Restore',
                      }}
                    />
                  ) : (
                    <RowActions
                      onView={() => navigate(`/barrister/matters/${c.id}`)}
                      onEdit={canChangeMatter ? () => setEditMatter(c) : undefined}
                      onDelete={canChangeMatter ? () => handleArchive(c) : undefined}
                      deleteLabel="Archive"
                      deleteConfirm={{
                        title: 'Archive matter?',
                        message: `Archive “${c.name}” (${c.number})? It will be removed from active lists but kept on record and can be restored later.`,
                        confirmLabel: 'Archive',
                      }}
                    />
                  )}
                </td>
              </tr>
            );})}
            {filtered.length === 0 && !(mattersLoading && allMatters.length === 0) && (
              <tr>
                <td colSpan={8} className="p-0">
                  {searching ? (
                    <div className="px-6 py-12 text-center text-sm text-muted-foreground">
                      {searchQ.isFetching ? 'Searching…' : `No matters match “${debouncedSearch}”.`}
                    </div>
                  ) : (
                    <EmptyState
                      icon={<Briefcase className="h-6 w-6" />}
                      title={tab === 'active' ? 'No active matters yet' : 'Nothing here yet'}
                      description={tab === 'active'
                        ? 'Create your first matter to start tracking hearings, tasks, documents and fees in one place.'
                        : 'Matters will appear here as your practice grows.'}
                      action={tab === 'active' && !archivedOnly && canOpenMatter ? (
                        <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => navigate('/barrister/matters/new')}>
                          {chambersMode.isSolo ? 'New matter' : 'New personal matter'}
                        </Button>
                      ) : undefined}
                    />
                  )}
                </td>
              </tr>
            )}

            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <TablePagination
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </Card>

      <EditMatterModal
        open={!!editMatter}
        matter={editMatter}
        onClose={() => setEditMatter(null)}
        // Ending a matter belongs to the close ceremony, which lives on the matter itself.
        onCloseMatter={(id) => { setEditMatter(null); navigate(`/barrister/matters/${id}?close=1`); }}
        onSave={async (id, patch) => {
          setEditMatter(null);
          if (await updateMatter(id, patch)) toast.success('Matter updated');
        }}
      />
    </div>
  );
}
